/**
 * stepViewsFlag — resolves `emuw-step-views` for the task pane.
 *
 * Story 26739's rollback lever. POSITIVE flag: an enabled row turns the step
 * views ON, an absent / disabled row leaves today's drawer body rendering
 * byte for byte unchanged — which is what `InstanceDetailDrawer.test.tsx`'s
 * pre-existing assertions prove.
 *
 * FAIL CLOSED in every direction: `false` while the fetch is in flight, and
 * `false` on any error (`orchestratorApi.getFeatureFlag` already swallows the
 * failure and returns `{ isEnabled: false }`).
 *
 * RESOLVED AT DRAWER LEVEL, NEVER INSIDE A BODY. The add-in renders two
 * independent inner trees (`LiveBody` and `TerminalBody`), and the
 * running-drawer work learned this the hard way: a flag read inside the live
 * tree left every completed / failed / cancelled run on the old surface.
 * The exported drawer component owns `push` for both trees, so it owns this.
 *
 * Shape copied verbatim from `runningDrawerFlag.ts`, including the
 * `refreshKey`: the first fetch on pane mount can land before the flag row
 * exists (or during an API bounce) and fail closed, and with `[]` that answer
 * stuck for the whole Outlook WebView lifetime. Re-reading on open / instance
 * is what makes flipping the SQL row take effect without a sideload restart.
 *
 * Nothing here touches `Office.context.*` — the drawer that calls it is
 * covered by jsdom tests where `Office` is undefined.
 */
import { useEffect, useState } from "react";
import { orchestratorApi } from "../services/orchestratorApi";

export const EMUW_STEP_VIEWS_FLAG = "emuw-step-views";

export function useStepViewsFlag(
  refreshKey?: string | number | boolean | null,
): boolean {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { isEnabled } = await orchestratorApi.getFeatureFlag(
          EMUW_STEP_VIEWS_FLAG,
        );
        if (!cancelled) setEnabled(isEnabled === true);
      } catch {
        // Fail closed — today's body renders.
        if (!cancelled) setEnabled(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  return enabled;
}

export default useStepViewsFlag;
